<template>
  <div
    data-component="base-item-info"
    :class="componentState"
  >
    <div
      v-if="icon"
      class="icon-wrapper"
    >
      <or-icon :icon="icon" />
    </div>

    <component
      :is="avatarType"
      v-else-if="person"
      :person="person"
      :avatar="avatar"
    />

    <div class="info">
      <div
        class="title"
        data-title
      >
        {{ title }}
      </div>

      <div
        v-if="description"
        class="description"
      >
        {{ description }}
      </div>

      <div
        v-if="status"
        class="status"
        data-status
        :class="statusState"
      >
        {{ status }}
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from '@vue/composition-api';
import { OrIconV3 as OrIcon } from '@onereach/ui-components-25';

import { AgentAvatar, ContactAvatar } from '@onereach/hitl-base-avatars';
import {
  AVATAR_PROP,
  PERSON_PROP,
  SIZE_PROP,
  STATUS_TYPE_PROP,
} from '../../constants';

export default defineComponent({
  name: 'BaseItemInfo',
  components: { OrIcon },
  props: {
    avatar: AVATAR_PROP,
    person: PERSON_PROP,
    icon: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
    status: {
      type: String,
      default: '',
    },
    statusType: STATUS_TYPE_PROP,
    size: SIZE_PROP,
    isDisabled: Boolean,
  },
  computed: {
    componentState() {
      return {
        [`is-${this.size}`]: true,
        'is-disabled': this.isDisabled,
      };
    },
    statusState() {
      return this.statusType ? { [`color-${this.statusType}`]: true } : {};
    },
    avatarType() {
      return this.person && ('id' in this.person) && this.person.role
        ? AgentAvatar
        : ContactAvatar;
    },
  },
});
</script>

<style lang="scss" scoped>
@use '@/styles' as *;

[data-component='base-item-info'] {
  --item-icon-size: 2.5rem;
  --item-title-font-size: var(--fs-1);
  --item-description-font-size: var(--fs-0);

  display: flex;
  overflow: hidden;

  &.is-mobile {
    --item-title-font-size: 1rem;
    --item-description-font-size: var(--fs-1);
  }

  &.is-disabled {
    .info .title,
    .info .description,
    .info .status {
      color: var(--or-c-disabled) !important;
    }

    .icon-wrapper {
      background: var(--or-c-disabled);

      .or-icon-v3 {
        color: var(--or-c-disabled);
      }
    }
  }

  .icon-wrapper {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    height: var(--item-icon-size);
    width: var(--item-icon-size);
    border-radius: 50%;
    background: var(--or-c-primary);

    .or-icon-v3 {
      color: var(--or-c-on-primary);
    }
  }

  .agent-avatar,
  .contact-avatar {
    cursor: inherit;
  }

  .info {
    display: flex;
    flex-direction: column;
    margin-left: var(--s-3);
    overflow: hidden;

    .title {
      @include text-overflow;
      font-size: var(--item-title-font-size);
      color: var(--or-c-on-background);
    }

    .description {
      color: var(--or-c-outline);
    }

    .description,
    .status {
      @include text-overflow;
      font-size: var(--item-description-font-size);
      line-height: 1.5;
    }

    .status {
      font-weight: var(--fw-1);
      color: var(--or-c-outline);

      &.color-neutral {
        color: var(--or-c-secondary);
      }

      &.color-success {
        color: var(--or-c-success);
      }

      &.color-warning {
        color: var(--or-c-warning);
      }

      &.color-error {
        color: var(--or-c-error);
      }
    }
  }
}
</style>
